import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { api, qs } from "@/lib/api";
import { formatCents, formatDate, formatInt } from "@/lib/format";
import { useIsAdmin } from "@/lib/session";
import { useToast } from "@/components/Toaster";
import { BrandMark } from "@/components/BrandMark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useContactNames } from "@/features/billing/useContactNames";
import { addressLines, HOLD_DAYS, methodLabel, PARTNER_STATUS, useAllAffiliates, usePrograms, type Affiliate, type AffiliateLink } from "./affiliates";
import { CommissionsTable, PayoutsTable } from "./AffiliatesPage";
import { PartnerDialog } from "./PartnerDialog";
import { PayoutDialog } from "./PayoutDialog";
import { LoadError } from "@/components/LoadError";

/** One partner: balances, payout details, links, commissions and payouts; change them or record a payout. */
export function AffiliateDetailPage() {
  const { affiliateId } = useParams({ from: "/affiliates/$affiliateId" });
  const id = Number(affiliateId);
  const isAdmin = useIsAdmin();
  const q = useQuery({ queryKey: ["affiliate", id], queryFn: () => api<Affiliate>(`/api/v1/affiliates/${id}`) });
  const links = useQuery({ queryKey: ["affiliate-links", id], queryFn: () => api<{ links: AffiliateLink[] }>("/api/v1/affiliate-links" + qs({ affiliate_id: id })) });
  const all = useAllAffiliates();
  const programs = usePrograms().data?.programs ?? [];
  const partners = all.data ?? [];
  const parent = partners.find((p) => p.id === q.data?.parent_affiliate_id);
  const recruits = partners.filter((p) => p.parent_affiliate_id === id);
  const name = useContactNames([q.data?.contact_id ?? 0, parent?.contact_id ?? 0, ...recruits.map((r) => r.contact_id)]);
  const [dialog, setDialog] = useState<"edit" | "pay" | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const approve = useMutation({
    mutationFn: () => api<Affiliate>(`/api/v1/affiliates/${id}`, { method: "PATCH", json: { status: "active" } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["affiliates"] });
      void qc.invalidateQueries({ queryKey: ["affiliate", id] });
      toast(`Approved ${r.code}: their links count from now on`);
    },
    onError: (e) => toast(e.message, "error"),
  });
  if (q.isLoading) return <Skeleton className="h-96" />;
  if (!q.data) return <p className="text-sm text-[var(--color-danger-fg)]">{q.error?.message ?? "Partner not found."}</p>;
  const a = q.data;
  const [label, variant] = PARTNER_STATUS[a.status] ?? [a.status, "muted" as const];
  const program = programs.find((p) => p.id === a.default_program_id);
  const waiting = a.unpaid_cents - a.payable_cents;
  return (
    <div className="mx-auto max-w-[1100px] space-y-3">
      <Link to="/affiliates" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Affiliates
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{a.code}</h1>
        <Badge variant={variant}>{label}</Badge>
        {a.keap_id && <span className="text-xs text-[var(--color-muted-foreground)]">from Keap</span>}
        <span className="ml-auto flex gap-2">
          {a.status === "pending" && (
            <Button size="sm" disabled={approve.isPending} onClick={() => approve.mutate()}>
              Approve
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setDialog("edit")}>
            Change
          </Button>
          {isAdmin ? (
            <Button size="sm" disabled={a.payable_cents <= 0} onClick={() => setDialog("pay")} title={a.payable_cents <= 0 ? "Nothing is payable right now" : undefined}>
              Record payout
            </Button>
          ) : (
            <span className="self-center text-xs text-[var(--color-warning-fg)]">Only an admin can record payouts.</span>
          )}
        </span>
      </div>
      <p className="text-sm">
        <Link to="/contacts/$contactId" params={{ contactId: String(a.contact_id) }} className="font-medium text-[var(--color-primary)] hover:underline">
          {name(a.contact_id)}
        </Link>
        <span className="text-[var(--color-muted-foreground)]">, partner since {formatDate(a.created_at)}</span>
      </p>
      {a.status === "pending" && (
        <p className="rounded-md border p-2 text-sm text-[var(--color-warning-fg)]">
          Signed up and waiting for approval. Their links don't count referrals and they earn nothing until you approve them.
        </p>
      )}
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Money</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-[var(--color-muted-foreground)]">Payable now</dt>
              <dd className="num font-semibold">{formatCents(a.payable_cents)}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Waiting ({HOLD_DAYS}-day refund window)</dt>
              <dd className="num">{formatCents(Math.max(0, waiting))}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Paid so far</dt>
              <dd className="num">{formatCents(a.paid_cents)}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Earned in all</dt>
              <dd className="num">{formatCents(a.earned_cents)}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Link clicks</dt>
              <dd className="num">{formatInt(a.clicks)}</dd>
            </dl>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Partner details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-[var(--color-muted-foreground)]">Program</dt>
              <dd>{program ? `${program.name}${program.active ? "" : " (off)"}` : a.default_program_id ? `#${a.default_program_id}` : "None"}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Recruited by</dt>
              <dd>
                {a.parent_affiliate_id ? (
                  <Link to="/affiliates/$affiliateId" params={{ affiliateId: String(a.parent_affiliate_id) }} className="text-[var(--color-primary)] hover:underline">
                    {parent ? `${parent.code} (${name(parent.contact_id)})` : `#${a.parent_affiliate_id}`}
                  </Link>
                ) : (
                  "Nobody"
                )}
              </dd>
              <dt className="text-[var(--color-muted-foreground)]">Recruited</dt>
              <dd>
                {recruits.length === 0
                  ? "Nobody"
                  : recruits.map((r, i) => (
                      <span key={r.id}>
                        {i > 0 && ", "}
                        <Link to="/affiliates/$affiliateId" params={{ affiliateId: String(r.id) }} className="text-[var(--color-primary)] hover:underline">
                          {r.code}
                        </Link>
                      </span>
                    ))}
              </dd>
              <dt className="text-[var(--color-muted-foreground)]">Usually paid by</dt>
              <dd>{methodLabel(a.payout_method)}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Payout email</dt>
              <dd className="break-all">{a.payout_email || "—"}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Check mailing address</dt>
              <dd>
                {addressLines(a.payout_address).length ? (
                  <address className="not-italic">
                    {addressLines(a.payout_address).map((l, i) => (
                      <span key={i} className="block">
                        {l}
                      </span>
                    ))}
                  </address>
                ) : (
                  "—"
                )}
              </dd>
              {a.payout_updated_at && (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">Payout details changed</dt>
                  <dd>{formatDate(a.payout_updated_at)}, by the partner</dd>
                </>
              )}
              {a.notes && (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">Notes</dt>
                  <dd className="whitespace-pre-wrap">{a.notes}</dd>
                </>
              )}
            </dl>
          </CardContent>
        </Card>
      </div>
      <Card className="overflow-x-auto">
        <CardHeader>
          <CardTitle>Links</CardTitle>
        </CardHeader>
        <CardContent>
          {links.isLoading ? (
            <Skeleton className="h-10" />
          ) : links.error ? (
            <LoadError className="px-0" what="links" error={links.error} onRetry={() => links.refetch()} />
          ) : (links.data?.links ?? []).length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              No named links. Their code link works on every brand: account.&lt;brand&gt;.com/a/{a.code}
            </p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {links.data!.links.map((l) => (
                <li key={l.id} className={l.active ? "flex flex-wrap items-center gap-2" : "row-dim flex flex-wrap items-center gap-2"}>
                  {l.brand_id ? <BrandMark id={l.brand_id} /> : null}
                  <span className="font-medium">{l.name || l.slug}</span>
                  <span className="text-xs text-[var(--color-muted-foreground)]">/a/{l.slug} goes to</span>
                  <span className="break-all text-xs">{l.destination_url}</span>
                  {!l.active && <Badge variant="muted">off</Badge>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <h2 className="pt-2 font-semibold">Commissions</h2>
      <CommissionsTable affiliateId={id} />
      <h2 className="pt-2 font-semibold">Payouts</h2>
      <PayoutsTable affiliateId={id} />
      {dialog === "edit" && <PartnerDialog partner={a} partners={partners} onClose={() => setDialog(null)} />}
      {dialog === "pay" && <PayoutDialog affiliate={a} name={name(a.contact_id)} onClose={() => setDialog(null)} />}
    </div>
  );
}
